import { apiClient } from './api';
import {
  PromptEvolutionSession,
  PromptEvolutionIteration,
  PromptEvolutionRequest,
  PromptEvolutionResponse,
  GeneratedPrompt,
  GeneratedImage,
  PromptEvolutionStats,
  getAgentById,
  LoraGenerationRequest,
  LoraGenerationResponse,
  LoraTrainingPrompt,
} from '../models/promptEvolution';

const SESSION_STORAGE_KEY = 'promptEvolutionSessions';

export class PromptEvolutionService {
  // Session Management
  static createSession(title: string, initialPrompt: string, description?: string): PromptEvolutionSession {
    const session: PromptEvolutionSession = {
      id: `prompt-evo-${Date.now()}`,
      title,
      description,
      initialPrompt,
      currentPrompt: initialPrompt,
      iterations: [],
      metadata: {
        totalIterations: 0,
        agentsUsed: [],
        exportHistory: [],
        tags: [],
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.saveSession(session);
    return session;
  }

  static saveSession(session: PromptEvolutionSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }
    
    session.updatedAt = new Date().toISOString();
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
  }

  static getSession(sessionId: string): PromptEvolutionSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === sessionId) || null;
  }

  static getAllSessions(): PromptEvolutionSession[] {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static deleteSession(sessionId: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filtered));
  }

  // Prompt Evolution using existing agent endpoints
  static async evolvePrompt(
    sessionId: string,
    agentType: string,
    customInstructions?: string
  ): Promise<PromptEvolutionResponse> {
    const session = this.getSession(sessionId);
    if (!session) {
      throw new Error('Session not found');
    }

    const agent = getAgentById(agentType);
    if (!agent) {
      throw new Error('Agent not found');
    }

    try {
      // Route to appropriate agent endpoint based on type
      let response: any;
      const currentPrompt = session.currentPrompt;

      switch (agentType) {
        case 'image-prompt-iterator':
          // Use existing image prompt iterator endpoint
          const imageResponse = await apiClient.post('/api/imageprompt/session/start', {
            initialPrompt: currentPrompt,
          });
          
          if (imageResponse.data.sessionId) {
            // Generate iteration
            const iterResponse = await apiClient.post(
              `/api/imageprompt/session/${imageResponse.data.sessionId}/iterate`,
              {
                agentType: 'Creative',
                additionalInstructions: customInstructions,
              }
            );
            
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: iterResponse.data.improvedPrompt || iterResponse.data.processedPrompt,
                  explanation: 'Enhanced for visual impact and artistic style',
                },
                {
                  id: `variant-2`,
                  prompt: this.generateVariation(currentPrompt, 'detailed'),
                  explanation: 'Added more descriptive details',
                },
                {
                  id: `variant-3`,
                  prompt: this.generateVariation(currentPrompt, 'atmospheric'),
                  explanation: 'Enhanced atmospheric and mood elements',
                },
              ],
            };
          }
          break;

        case 'story-generator':
          // Use story generator endpoint
          const storyResponse = await apiClient.post('/api/agent/story/start', {
            concept: currentPrompt,
            genre: 'general',
            tone: 'balanced',
          });
          
          if (storyResponse.data.sessionId) {
            // Call iterate for story development
            const storyIterResponse = await apiClient.post(
              `/api/agent/story/${storyResponse.data.sessionId}/iterate`,
              {
                feedback: customInstructions || 'develop narrative elements',
                direction: 'expand story world',
              }
            );
            
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: storyIterResponse.data.improvedStory?.substring(0, 300) || storyResponse.data.initialStory?.substring(0, 300) || currentPrompt,
                  explanation: 'Narrative-enhanced version',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, with rich world-building, compelling plot twists, emotional depth`,
                  explanation: 'Story structure focus',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, character-driven narrative, dialogue-rich, immersive setting`,
                  explanation: 'Character development focus',
                },
              ],
            };
          }
          break;

        case 'character-agent':
          // Use character agent endpoint
          const charResponse = await apiClient.post('/api/character/session/start', {
            characterConcept: currentPrompt,
            genre: 'general',
            style: 'detailed',
            purpose: 'prompt enhancement',
          });
          
          if (charResponse.data.sessionId) {
            // Call iterate for character development
            const charIterResponse = await apiClient.post(
              `/api/character/session/${charResponse.data.sessionId}/iterate`,
              {
                feedback: customInstructions || 'develop character traits and backstory',
                aspectToRefine: 'all',
              }
            );
            
            const charData = charIterResponse.data.character || charResponse.data.character || charResponse.data;
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: `${currentPrompt}, featuring ${charData.name || 'protagonist'} with ${charData.traits?.join(', ') || 'complex personality traits, internal conflicts'}`,
                  explanation: 'Character-centric narrative',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, character with rich backstory: ${charData.backstory || 'mysterious past, hidden motivations'}`,
                  explanation: 'Backstory-enhanced version',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, ${charData.appearance || 'distinctive appearance'}, ${charData.personality || 'multi-dimensional personality'}`,
                  explanation: 'Visual and personality focus',
                },
              ],
            };
          }
          break;

        case 'scene-agent':
          // Use scene agent endpoint  
          const sceneResponse = await apiClient.post('/api/scene/session/start', {
            InitialPrompt: currentPrompt,
            genre: 'general',
            style: 'cinematic',
            moodAndTone: 'dynamic',
          });
          
          if (sceneResponse.data.sessionId) {
            // Call iterate for scene enhancement
            const sceneIterResponse = await apiClient.post(
              `/api/scene/session/${sceneResponse.data.sessionId}/iterate`,
              {
                feedback: customInstructions || 'enhance atmosphere and sensory details',
                aspectToRefine: 'all',
              }
            );
            
            const sceneData = sceneIterResponse.data.scene || sceneResponse.data.scene || sceneResponse.data;
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: `${currentPrompt}, ${sceneData.atmosphere || 'dramatic lighting, moody atmosphere'}, ${sceneData.sensoryDetails || 'rich sensory details'}`,
                  explanation: 'Atmospheric enhancement',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, set in ${sceneData.location || 'evocative setting'}, ${sceneData.timeOfDay || 'golden hour lighting'}`,
                  explanation: 'Setting and lighting focus',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, ${sceneData.mood || 'tense atmosphere'}, ${sceneData.weatherConditions || 'dramatic weather'}`,
                  explanation: 'Mood and environment',
                },
              ],
            };
          }
          break;

        case 'video-prompt':
          // Use video prompt iterator endpoint
          const videoResponse = await apiClient.post('/api/videoprompt/session/start', {
            initialPrompt: currentPrompt,
          });
          
          if (videoResponse.data.sessionId) {
            const iterResponse = await apiClient.post(
              `/api/videoprompt/session/${videoResponse.data.sessionId}/iterate`,
              {
                agentType: 'Creative',
                additionalInstructions: customInstructions,
              }
            );
            
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: iterResponse.data.improvedPrompt || iterResponse.data.processedPrompt || currentPrompt,
                  explanation: 'Optimized for video generation',
                },
                {
                  id: `variant-2`,
                  prompt: this.generateVariation(currentPrompt, 'cinematic'),
                  explanation: 'Cinematic variation',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, dynamic camera movement, cinematic lighting`,
                  explanation: 'Added motion elements',
                },
              ],
            };
          }
          break;

        case 'abstract-thinker':
          // Use abstract thinker endpoint with correct path and field name
          const abstractResponse = await apiClient.post('/api/thinker/abstract/abstract-thinking/start', {
            initialQueryOrTopic: currentPrompt,
            depth: 'deep',
          });
          
          if (abstractResponse.data.sessionId) {
            const iterResponse = await apiClient.post(
              `/api/thinker/abstract/abstract-thinking/${abstractResponse.data.sessionId}/iterate`,
              {
                direction: customInstructions || 'expand conceptually',
              }
            );
            
            // Extract key themes and concepts from the abstract thinker's analysis
            const analysis = iterResponse.data.finalConclusion || iterResponse.data.thought || '';
            const thinkingProcess = iterResponse.data.thinkingProcess || [];
            
            // Parse the analysis to extract meaningful prompt variations
            response = {
              success: true,
              generatedPrompts: this.parseAbstractThinkerResponse(analysis, currentPrompt, thinkingProcess),
            };
          }
          break;

        case 'ux-designer':
          // Use UX designer endpoint
          const uxResponse = await apiClient.post('/api/uxdesign/start', {
            projectName: 'Design Project',
            projectDescription: currentPrompt,
            targetAudience: 'general users',
          });
          
          if (uxResponse.data.sessionId) {
            // Call iterate for design refinement
            const uxIterResponse = await apiClient.post(
              `/api/uxdesign/${uxResponse.data.sessionId}/iterate`,
              {
                feedback: customInstructions || 'enhance user experience and visual design',
                focusArea: 'user-flow',
              }
            );
            
            const uxData = uxIterResponse.data.design || uxIterResponse.data;
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: `${currentPrompt}, ${uxData.designPrinciples || 'user-centered design, intuitive interface'}, ${uxData.visualStyle || 'clean modern aesthetic'}`,
                  explanation: 'UX-optimized design',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, accessible design with ${uxData.accessibilityFeatures || 'WCAG compliance, screen reader support'}, clear navigation`,
                  explanation: 'Accessibility-first approach',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, ${uxData.interactionPatterns || 'modern UI patterns'}, responsive layout, ${uxData.colorScheme || 'harmonious color palette'}`,
                  explanation: 'Modern UI/UX design',
                },
              ],
            };
          }
          break;

        case 'coding-assistant':
          // Use coding agent endpoint
          const codingResponse = await apiClient.post('/api/agent/coding/start', {
            purpose: 'enhance',
            request: currentPrompt,
            language: 'typescript',
            framework: 'react',
          });
          
          if (codingResponse.data.sessionId) {
            // Call iterate for code enhancement
            const codingIterResponse = await apiClient.post(
              `/api/agent/coding/${codingResponse.data.sessionId}/iterate`,
              {
                feedback: customInstructions || 'improve code quality and architecture',
                focus: 'best-practices',
              }
            );
            
            const codeData = codingIterResponse.data.code || codingIterResponse.data;
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: `${currentPrompt}, ${codeData.architecture || 'clean architecture, SOLID principles'}, ${codeData.patterns || 'design patterns applied'}`,
                  explanation: 'Architecture-focused implementation',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, ${codeData.performance || 'optimized algorithms, efficient data structures'}, scalable architecture`,
                  explanation: 'Performance-optimized code',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, ${codeData.testing || 'comprehensive unit tests, integration tests'}, ${codeData.errorHandling || 'robust error handling'}`,
                  explanation: 'Quality and reliability focus',
                },
              ],
            };
          }
          break;

        case 'scientific-agent':
          try {
            // Use physics agent as the default scientific agent
            const sciResponse = await apiClient.post('/api/physics/session/start', {
              query: currentPrompt,
              domain: 'Physics', // Default domain
              detailDomain: 'General Concepts',
            });
            
            if (sciResponse.data.sessionId) {
              // Generate iteration for more variations
              const iterResponse = await apiClient.post(
                `/api/physics/session/${sciResponse.data.sessionId}/iterate`,
                {
                  userInput: customInstructions || 'enhance scientific accuracy',
                  domain: 'Applied Physics',
                  detailDomain: 'Theoretical Framework',
                }
              );
              
              const sciData = iterResponse.data.visualization || iterResponse.data;
              response = {
                success: true,
                generatedPrompts: [
                  {
                    id: `variant-1`,
                    prompt: `${currentPrompt}, ${sciData?.content?.detailedExplanation || 'with quantum physics principles applied'}`,
                    explanation: 'Physics-enhanced interpretation',
                  },
                  {
                    id: `variant-2`,
                    prompt: `${currentPrompt}, incorporating scientific accuracy and empirical research`,
                    explanation: 'Research-based approach',
                  },
                  {
                    id: `variant-3`,
                    prompt: `${currentPrompt}, with technical specifications and theoretical framework`,
                    explanation: 'Technical precision focus',
                  },
                ],
              };
            }
          } catch (error) {
            // Fallback to local generation if scientific endpoint is not available
            console.log('Scientific agent endpoint not available, using local generation');
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: `${currentPrompt}, with scientific accuracy and empirical research methodology, peer-reviewed data sources`,
                  explanation: 'Scientific research approach',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, incorporating physics principles, mathematical models, and theoretical frameworks`,
                  explanation: 'Physics and mathematics focus',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, using scientific method, hypothesis testing, experimental design considerations`,
                  explanation: 'Experimental science perspective',
                },
              ],
            };
          }
          break;

        case 'urban-culture':
          // Use urban culture agent - defaults to street art for prompt evolution
          const urbanResponse = await apiClient.post('/api/imageprompt/urban/streetart/session/start', {
            InitialConcept: currentPrompt,
            style: 'contemporary',
            era: '2020s',
            region: 'global',
          });
          
          if (urbanResponse.data.sessionId) {
            const iterResponse = await apiClient.post(
              `/api/imageprompt/urban/streetart/session/${urbanResponse.data.sessionId}/iterate`,
              {
                feedback: customInstructions || 'enhance urban aesthetic',
                agentType: 'StreetArtCulture', // Use culture-focused subagent
                temperature: 0.8,
              }
            );
            
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: iterResponse.data.improvedPrompt || iterResponse.data.processedPrompt || currentPrompt,
                  explanation: 'Street art culture enhancement',
                },
                {
                  id: `variant-2`,
                  prompt: this.generateVariation(currentPrompt, 'urban'),
                  explanation: 'Urban aesthetic variation',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, graffiti art style, urban environment, street culture`,
                  explanation: 'Street art focused',
                },
              ],
            };
          }
          break;

        case 'fashion-expert':
          // Use fashion multi-agent endpoint
          const fashionResponse = await apiClient.post('/api/fashion/multi-agent/start', {
            brandName: 'Fashion Evolution',
            collectionTheme: currentPrompt,
            targetAudience: 'fashion-forward individuals',
          });
          
          if (fashionResponse.data.designs && fashionResponse.data.designs.length > 0) {
            const designs = fashionResponse.data.designs;
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: designs[0]?.description || `${currentPrompt}, haute couture fashion, designer clothing`,
                  explanation: 'High fashion interpretation',
                },
                {
                  id: `variant-2`,
                  prompt: designs[1]?.description || `${currentPrompt}, trendy streetwear, contemporary style`,
                  explanation: 'Modern fashion approach',
                },
                {
                  id: `variant-3`,
                  prompt: designs[2]?.description || `${currentPrompt}, elegant attire, sophisticated styling`,
                  explanation: 'Elegant fashion vision',
                },
              ],
            };
          } else {
            // Fallback to local generation
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: `${currentPrompt}, haute couture fashion, designer clothing, runway style`,
                  explanation: 'High fashion approach',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, trendy streetwear, contemporary fashion, style-forward`,
                  explanation: 'Modern fashion trends',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, elegant attire, sophisticated styling, fashion photography`,
                  explanation: 'Elegant fashion focus',
                },
              ],
            };
          }
          break;

        case 'marketing-agent':
          // Use content marketing agent endpoint
          const marketingResponse = await apiClient.post('/api/marketing/session/start', {
            initialPrompt: currentPrompt,
            marketingType: 'brand',
          });
          
          if (marketingResponse.data.sessionId) {
            const iterResponse = await apiClient.post(
              `/api/marketing/session/${marketingResponse.data.sessionId}/iterate`,
              {
                feedback: customInstructions || 'enhance marketing appeal',
                focusArea: 'emotional_appeal',
              }
            );
            
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: iterResponse.data.improvedContent || currentPrompt,
                  explanation: 'Marketing-optimized',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, compelling call-to-action, target audience focused`,
                  explanation: 'Audience-targeted',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, brand storytelling, emotional connection`,
                  explanation: 'Brand narrative',
                },
              ],
            };
          }
          break;

        case 'journalism-agent':
          // Use journalism multi-agent endpoint
          const journalismResponse = await apiClient.post('/api/journalism/multi-agent/start', {
            topic: currentPrompt,
            articleType: 'feature',
            tone: 'informative',
          });
          
          if (journalismResponse.data.article) {
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: journalismResponse.data.headline || currentPrompt,
                  explanation: 'Journalistic headline',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, investigative journalism angle, fact-based reporting`,
                  explanation: 'Investigative approach',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, human interest story, compelling narrative`,
                  explanation: 'Human interest angle',
                },
              ],
            };
          }
          break;

        case 'comics-specialist':
          // Use comic book agent endpoint
          const comicsResponse = await apiClient.post('/api/comicbook/session/start', {
            title: 'Prompt Evolution',
            genre: 'action',
            ConceptDescription: currentPrompt,
          });
          
          if (comicsResponse.data.sessionId) {
            try {
              // Try to call iterate endpoint
              const comicsIterResponse = await apiClient.post(
                `/api/comicbook/session/${comicsResponse.data.sessionId}/iterate`,
                {
                  feedback: customInstructions || 'enhance visual storytelling',
                  panelRequest: 'dynamic composition',
                }
              );
              
              response = {
                success: true,
                generatedPrompts: [
                  {
                    id: `variant-1`,
                    prompt: comicsIterResponse.data.improvedDescription || `${currentPrompt}, comic book style, dynamic action panels`,
                    explanation: 'Enhanced comic book narrative',
                  },
                  {
                    id: `variant-2`,
                    prompt: `${currentPrompt}, manga aesthetic, expressive characters, speed lines`,
                    explanation: 'Manga-style visualization',
                  },
                  {
                    id: `variant-3`,
                    prompt: `${currentPrompt}, graphic novel format, cinematic panels, noir atmosphere`,
                    explanation: 'Graphic novel cinematography',
                  },
                ],
              };
            } catch (iterError: any) {
              // If iterate endpoint doesn't exist, use start response data
              console.log('Comics iterate endpoint not available, using start response');
              const comicData = comicsResponse.data;
              
              response = {
                success: true,
                generatedPrompts: [
                  {
                    id: `variant-1`,
                    prompt: `${currentPrompt}, comic book style with ${comicData.artStyle || 'dynamic action panels'}, ${comicData.panelLayout || 'sequential storytelling'}`,
                    explanation: 'Classic comic book style',
                  },
                  {
                    id: `variant-2`,
                    prompt: `${currentPrompt}, manga aesthetic with ${comicData.characterDesign || 'expressive characters'}, speed lines, dramatic reactions`,
                    explanation: 'Manga-style visualization',
                  },
                  {
                    id: `variant-3`,
                    prompt: `${currentPrompt}, graphic novel format, ${comicData.atmosphere || 'noir lighting'}, cinematic framing, mature themes`,
                    explanation: 'Graphic novel approach',
                  },
                ],
              };
            }
          }
          break;

        case 'animation-storyboard':
          // Use storyboard agent endpoint
          const storyboardResponse = await apiClient.post('/api/storyboard/session/start', {
            title: 'Animation Project',
            concept: currentPrompt,
            genre: 'animation',
            style: 'dynamic',
          });
          
          if (storyboardResponse.data.sessionId) {
            // Storyboard doesn't have standard iterate, use createScene instead
            const sceneResponse = await apiClient.post(
              `/api/storyboard/session/${storyboardResponse.data.sessionId}/scene/create`,
              {
                sceneDescription: customInstructions || `Create dynamic scene based on: ${currentPrompt}`,
                sceneNumber: 1,
                duration: 30,
              }
            );
            
            const storyboardData = sceneResponse.data.storyboard || storyboardResponse.data.storyboard || storyboardResponse.data;
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: `${currentPrompt}, storyboarded with dynamic keyframes, smooth transitions, cinematic camera angles`,
                  explanation: 'Animation-optimized storyboard',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, establishing shot transitioning to close-ups, wide angles for context, precise timing marks`,
                  explanation: 'Professional storyboard format',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, fluid character movement, dynamic action sequences, visual effects integration`,
                  explanation: 'Motion and effects focus',
                },
              ],
            };
          }
          break;

        case 'music-visualizer':
          // Use music prompt iterator endpoint with correct field name
          const musicResponse = await apiClient.post('/api/music-prompt/session/start', {
            concept: currentPrompt,
          });
          
          if (musicResponse.data.sessionId) {
            // Music prompt API uses specific endpoints, not generic iterate
            // Use mood-prompt for visualization concepts
            const iterResponse = await apiClient.post(
              `/api/music-prompt/mood-prompt`,
              {
                sessionId: musicResponse.data.sessionId,
                moodDescription: customInstructions || 'create visual representation of sound',
                visualElements: true,
              }
            );
            
            response = {
              success: true,
              generatedPrompts: [
                {
                  id: `variant-1`,
                  prompt: iterResponse.data.generatedPrompt || `${currentPrompt}, visualized as flowing colors and shapes`,
                  explanation: 'Music visualization concept',
                },
                {
                  id: `variant-2`,
                  prompt: `${currentPrompt}, synesthetic visualization, rhythm translated to motion, melody to color`,
                  explanation: 'Synesthetic interpretation',
                },
                {
                  id: `variant-3`,
                  prompt: `${currentPrompt}, audio waveform aesthetics, frequency spectrum visualization, beat-reactive elements`,
                  explanation: 'Technical visualization',
                },
              ],
            };
          }
          break;

        case 'paleontology-expert':
          // Paleontology agent - use generic variations since endpoint might not be available
          response = {
            success: true,
            generatedPrompts: [
              {
                id: `variant-1`,
                prompt: `${currentPrompt}, prehistoric setting, Mesozoic era, scientifically accurate paleontology`,
                explanation: 'Paleontologically accurate',
              },
              {
                id: `variant-2`,
                prompt: `${currentPrompt}, ancient environment, fossil details, geological strata`,
                explanation: 'Fossil and geology focused',
              },
              {
                id: `variant-3`,
                prompt: `${currentPrompt}, prehistoric life forms, Cretaceous period, paleontological reconstruction`,
                explanation: 'Prehistoric life reconstruction',
              },
            ],
          };
          break;

        default:
          // For agents without specific endpoints, use a generic approach
          response = {
            success: true,
            generatedPrompts: this.generateGenericVariations(currentPrompt, agent.name, customInstructions),
          };
      }

      // Create iteration record
      const iteration: PromptEvolutionIteration = {
        id: `iteration-${Date.now()}`,
        iterationNumber: session.iterations.length + 1,
        agentType,
        agentName: agent.name,
        inputPrompt: currentPrompt,
        generatedPrompts: response.generatedPrompts,
        customInstructions,
        timestamp: new Date().toISOString(),
        // Store the raw agent response for debugging/display
        agentResponse: response as any,
      };

      // Update session
      session.iterations.push(iteration);
      session.metadata.totalIterations++;
      if (!session.metadata.agentsUsed.includes(agentType)) {
        session.metadata.agentsUsed.push(agentType);
      }
      this.saveSession(session);

      return {
        success: true,
        iterationId: iteration.id,
        generatedPrompts: response.generatedPrompts,
        agentInsights: agent.description,
      };

    } catch (error: any) {
      console.error('Evolution error:', error);
      
      // Fallback to local generation if API fails
      const fallbackPrompts = this.generateGenericVariations(
        session.currentPrompt,
        agent.name,
        customInstructions
      );

      const iteration: PromptEvolutionIteration = {
        id: `iteration-${Date.now()}`,
        iterationNumber: session.iterations.length + 1,
        agentType,
        agentName: agent.name,
        inputPrompt: session.currentPrompt,
        generatedPrompts: fallbackPrompts,
        customInstructions,
        timestamp: new Date().toISOString(),
        // Store error information
        agentResponse: {
          success: false,
          error: error.message || 'Unknown error occurred',
          agentType,
        },
      };

      session.iterations.push(iteration);
      session.metadata.totalIterations++;
      if (!session.metadata.agentsUsed.includes(agentType)) {
        session.metadata.agentsUsed.push(agentType);
      }
      this.saveSession(session);

      return {
        success: true,
        iterationId: iteration.id,
        generatedPrompts: fallbackPrompts,
        agentInsights: agent.description,
      };
    }
  }

  // Helper method to generate variations locally
  private static generateVariation(prompt: string, style: string): string {
    const variations: Record<string, (p: string) => string> = {
      detailed: (p) => `${p}, highly detailed, intricate details, comprehensive view`,
      atmospheric: (p) => `${p}, moody atmosphere, dramatic lighting, immersive environment`,
      narrative: (p) => `${p}, telling a story, narrative elements, emotional depth`,
      character: (p) => `${p}, with compelling characters, personality traits visible, expressive`,
      philosophical: (p) => `${p}, exploring deeper meanings, conceptual layers, thought-provoking`,
      abstract: (p) => `${p}, abstract interpretation, symbolic elements, conceptual art`,
      cinematic: (p) => `${p}, cinematic composition, movie-like quality, dramatic angles`,
      urban: (p) => `${p}, urban setting, city vibes, street culture aesthetic`,
    };

    return variations[style]?.(prompt) || prompt;
  }

  private static parseAbstractThinkerResponse(
    analysis: string,
    originalPrompt: string,
    thinkingProcess: any[]
  ): GeneratedPrompt[] {
    const prompts: GeneratedPrompt[] = [];
    
    // Extract key themes from the analysis - looking for specific conceptual frameworks
    const hasSymbiosis = /symbiosis|augmentation|organic.*synthetic|bio-technological.*fusion/i.test(analysis);
    const hasRebellion = /rebellion|defiance|resistance|rebel|opposition.*power/i.test(analysis);
    const hasSurvival = /survival|adaptation|resilience|scaveng|resilient.*character/i.test(analysis);
    const hasEnvironmental = /environment|ecosystem|collapse|degradation|polluted.*environment/i.test(analysis);
    const hasTechnoTribal = /tribal|tattoo|personalized.*modification|identity.*markers/i.test(analysis);
    const hasDystopian = /dystopian|decay|corporate.*dominance|oppressive.*systems/i.test(analysis);
    const hasPhilosophical = /anthropocentrism|philosophical|ethical.*questions|moral.*implications/i.test(analysis);
    const hasGuerrillaWarfare = /guerrilla|stealth|unconventional.*methods|disrupt.*systems/i.test(analysis);
    
    // First prompt: Focus on the dominant theme
    if (hasSymbiosis && hasTechnoTribal) {
      prompts.push({
        id: `variant-1`,
        prompt: `${originalPrompt}, featuring seamless bio-technological symbiosis where organic exoskeleton merges with scavenged tech implants, iridescent chitin plating interwoven with fiber optic neural networks, tribal scarification patterns that glow with data streams`,
        explanation: 'Symbiotic Tech-Organic Fusion - Emphasizes the merger of biological and technological elements with cultural identity markers',
      });
    } else if (hasRebellion && hasGuerrillaWarfare) {
      prompts.push({
        id: `variant-1`,
        prompt: `${originalPrompt}, embodying guerrilla resistance fighter aesthetics, wearing improvised armor from corporate waste, displaying defiant stance with makeshift weapons, tribal war paint made from toxic runoff, scarred from battles against oppressive systems`,
        explanation: 'Guerrilla Rebel Aesthetic - Focuses on active resistance using unconventional warfare tactics',
      });
    } else if (hasSurvival && hasEnvironmental) {
      prompts.push({
        id: `variant-1`,
        prompt: `${originalPrompt}, adapted to thrive in toxic wastelands, bio-luminescent mutations for navigating polluted darkness, filtration organs visible through translucent chitin, carrying scavenged survival gear integrated into natural armor`,
        explanation: 'Environmental Survivor - Highlights adaptation to harsh, polluted cyberpunk environments',
      });
    } else {
      prompts.push({
        id: `variant-1`,
        prompt: `${originalPrompt}, conceptually reimagined as symbol of marginalized persistence, challenging anthropocentric hierarchies through unconventional existence`,
        explanation: 'Philosophical Rebel - Abstract exploration of resistance and value systems',
      });
    }
    
    // Second prompt: Combine multiple themes
    if (hasDystopian && hasPhilosophical) {
      prompts.push({
        id: `variant-2`,
        prompt: `${originalPrompt}, set against decaying corporate megastructures, representing nature's revenge on technological hubris, carrying encrypted data stolen from oppressive regimes, leaving graffiti messages of hope in forgotten corners`,
        explanation: 'Dystopian Philosophy - Explores deeper meanings of rebellion in technological decay',
      });
    } else if (hasRebellion && hasSurvival) {
      prompts.push({
        id: `variant-2`,
        prompt: `${originalPrompt}, battle-scarred veteran of underground resistance, wearing trophies from defeated corporate drones, enhanced with black market augmentations, leading swarm intelligence networks against surveillance states`,
        explanation: 'Resistance Survivor - Combines survival instincts with organized rebellion',
      });
    } else {
      prompts.push({
        id: `variant-2`,
        prompt: `${originalPrompt}, reimagined with enhanced narrative complexity, featuring unexpected character depth and moral ambiguity`,
        explanation: 'Narrative Enhancement - Adds layers of storytelling complexity',
      });
    }
    
    // Third prompt: Extract unique insights from the thinking process
    if (thinkingProcess && thinkingProcess.length > 0) {
      // Look for specific scenarios mentioned in the analysis
      const hasBioWeapon = /bio-engineered.*weapon|weapon.*gone.*rogue/i.test(analysis);
      const hasDigitalControl = /digitally.*controlled|controlled.*proxy|hacker.*proxy/i.test(analysis);
      const hasNaturalAdaptation = /naturally.*adapted|evolved.*to.*thrive/i.test(analysis);
      
      if (hasBioWeapon) {
        prompts.push({
          id: `variant-3`,
          prompt: `${originalPrompt}, originally bio-engineered as corporate weapon now gone rogue, displaying unpredictable behavior patterns, carrying dormant viral payloads, leaving trail of system corruptions, hunted by creator corporations`,
          explanation: 'Rogue Bioweapon - Explores themes of unintended consequences and corporate hubris',
        });
      } else if (hasDigitalControl) {
        prompts.push({
          id: `variant-3`,
          prompt: `${originalPrompt}, neural-linked to underground hacker collective, serving as physical proxy for digital rebellion, antennae array receiving encrypted commands, executing coordinated strikes on corporate infrastructure`,
          explanation: 'Digital Proxy - Bridges physical and digital resistance movements',
        });
      } else if (hasNaturalAdaptation) {
        prompts.push({
          id: `variant-3`,
          prompt: `${originalPrompt}, naturally evolved in toxic urban environments without augmentation, representing nature's resilience against technological excess, displaying unique survival traits from generations of adaptation`,
          explanation: 'Natural Evolution - Pure biological adaptation without technological enhancement',
        });
      } else {
        // Fallback: Extract unique visual and conceptual elements
        const uniqueElements = this.extractUniqueElements(thinkingProcess);
        prompts.push({
          id: `variant-3`,
          prompt: `${originalPrompt}, ${uniqueElements}`,
          explanation: 'Conceptual Synthesis - Combines extracted thematic elements from philosophical analysis',
        });
      }
    } else {
      // If no thinking process, create a variant based on the analysis summary
      prompts.push({
        id: `variant-3`,
        prompt: `${originalPrompt}, multifaceted symbol challenging established norms, embodying complex ethical questions about technological progress, survival in dystopian futures, and the value of unconventional life forms`,
        explanation: 'Thematic Integration - Synthesizes core philosophical themes',
      });
    }
    
    return prompts;
  }
  
  private static extractUniqueElements(thinkingProcess: any[]): string {
    const elements: string[] = [];
    
    // Extract interesting concepts from the thinking process
    thinkingProcess.forEach(step => {
      const output = step.output || '';
      
      // Look for visual and aesthetic elements
      if (/bio-luminescent|iridescent|glowing/i.test(output)) {
        elements.push('bio-luminescent exoskeleton details');
      }
      if (/antenna.*array|enhanced.*perception|sensory/i.test(output)) {
        elements.push('enhanced sensory arrays and perception systems');
      }
      if (/scavenged.*gear|mismatched.*equipment|improvised/i.test(output)) {
        elements.push('improvised technological modifications');
      }
      if (/art.*style|aesthetic|visual/i.test(output)) {
        elements.push('distinctive visual aesthetic');
      }
      
      // Look for conceptual elements
      if (/david.*goliath|underdog/i.test(output)) {
        elements.push('underdog narrative symbolism');
      }
      if (/virus|malware|disruption/i.test(output)) {
        elements.push('system disruption capabilities');
      }
      if (/marginalized|oppressed|shadow/i.test(output)) {
        elements.push('representing marginalized resistance');
      }
      if (/bio-hacking|DIY.*biology|genetic/i.test(output)) {
        elements.push('DIY bio-modification aesthetic');
      }
    });
    
    return elements.length > 0 
      ? elements.join(', ')
      : 'multi-layered conceptual exploration, thematic depth';
  }

  private static generateGenericVariations(
    prompt: string,
    agentName: string,
    customInstructions?: string
  ): GeneratedPrompt[] {
    const instruction = customInstructions ? `, ${customInstructions}` : '';
    
    return [
      {
        id: `variant-1`,
        prompt: `${prompt}, enhanced by ${agentName} expertise${instruction}`,
        explanation: `Applied ${agentName} specialization`,
      },
      {
        id: `variant-2`,
        prompt: `${prompt}, with professional ${agentName} perspective${instruction}`,
        explanation: `Professional enhancement from ${agentName}`,
      },
      {
        id: `variant-3`,
        prompt: `${prompt}, optimized for best results by ${agentName}${instruction}`,
        explanation: `Optimized version by ${agentName}`,
      },
    ];
  }

  // Select a prompt and update current
  static selectPrompt(sessionId: string, iterationId: string, promptIndex: number): void {
    const session = this.getSession(sessionId);
    if (!session) return;

    const iteration = session.iterations.find(i => i.id === iterationId);
    if (!iteration || !iteration.generatedPrompts[promptIndex]) return;

    iteration.selectedPromptIndex = promptIndex;
    session.currentPrompt = iteration.generatedPrompts[promptIndex].prompt;
    this.saveSession(session);
  }

  // Image generation
  static async generateImage(sessionId: string, prompt: string, provider: string): Promise<GeneratedImage> {
    const image: GeneratedImage = {
      id: `img-${Date.now()}`,
      promptUsed: prompt,
      iterationId: '',
      provider,
      generatedAt: new Date().toISOString(),
    };

    const session = this.getSession(sessionId);
    if (session) {
      if (!session.metadata.generatedImages) {
        session.metadata.generatedImages = [];
      }
      session.metadata.generatedImages.push(image);
      this.saveSession(session);
    }

    return image;
  }

  // Export functionality
  static exportSession(session: PromptEvolutionSession, format: 'json' | 'markdown' | 'text' = 'json'): void {
    let content: string;
    let filename: string;
    let mimeType: string;

    switch (format) {
      case 'markdown':
        content = this.generateMarkdownExport(session);
        filename = `prompt-evolution-${session.id}.md`;
        mimeType = 'text/markdown';
        break;
      case 'text':
        content = this.generateTextExport(session);
        filename = `prompt-evolution-${session.id}.txt`;
        mimeType = 'text/plain';
        break;
      default:
        content = JSON.stringify(session, null, 2);
        filename = `prompt-evolution-${session.id}.json`;
        mimeType = 'application/json';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);

    // Update export history
    session.metadata.exportHistory.push({
      exportedAt: new Date().toISOString(),
      format,
      includesImages: false,
    });
    this.saveSession(session);
  }

  private static generateMarkdownExport(session: PromptEvolutionSession): string {
    let content = `# Prompt Evolution Session: ${session.title}\n\n`;
    content += `**Created:** ${new Date(session.createdAt).toLocaleString()}\n`;
    content += `**Description:** ${session.description || 'N/A'}\n\n`;
    content += `## Initial Prompt\n${session.initialPrompt}\n\n`;
    content += `## Current Prompt\n${session.currentPrompt}\n\n`;
    content += `## Evolution History\n\n`;

    session.iterations.forEach((iteration, index) => {
      content += `### Iteration ${index + 1} - ${iteration.agentName}\n`;
      content += `**Input:** ${iteration.inputPrompt}\n\n`;
      if (iteration.customInstructions) {
        content += `**Instructions:** ${iteration.customInstructions}\n\n`;
      }
      content += `**Generated Variants:**\n\n`;
      
      iteration.generatedPrompts.forEach((prompt, i) => {
        const selected = iteration.selectedPromptIndex === i ? ' ✓' : '';
        content += `${i + 1}. ${prompt.prompt}${selected}\n`;
        if (prompt.explanation) {
          content += `   *${prompt.explanation}*\n`;
        }
        content += '\n';
      });
    });

    return content;
  }

  private static generateTextExport(session: PromptEvolutionSession): string {
    let content = `PROMPT EVOLUTION SESSION: ${session.title}\n`;
    content += `${'='.repeat(50)}\n\n`;
    content += `Initial: ${session.initialPrompt}\n\n`;
    content += `Current: ${session.currentPrompt}\n\n`;
    content += `Iterations:\n`;
    
    session.iterations.forEach((iteration, index) => {
      content += `\n${index + 1}. ${iteration.agentName}\n`;
      iteration.generatedPrompts.forEach((prompt, i) => {
        const selected = iteration.selectedPromptIndex === i ? ' [SELECTED]' : '';
        content += `   - ${prompt.prompt}${selected}\n`;
      });
    });

    return content;
  }

  // LORA Training Prompt Generation
  static async generateLoraPrompts(
    basePrompt: string,
    numberOfPrompts: number,
    promptStyle: 'short' | 'detailed'
  ): Promise<LoraGenerationResponse> {
    try {
      const variations = this.generateLoraVariations(basePrompt, numberOfPrompts, promptStyle);
      
      return {
        success: true,
        prompts: variations,
        metadata: {
          basePrompt,
          style: promptStyle,
          generatedAt: new Date().toISOString(),
        },
      };
    } catch (error: any) {
      return {
        success: false,
        prompts: [],
        error: error.message || 'Failed to generate LORA prompts',
      };
    }
  }

  private static generateLoraVariations(
    basePrompt: string,
    count: number,
    style: 'short' | 'detailed'
  ): LoraTrainingPrompt[] {
    const prompts: LoraTrainingPrompt[] = [];
    
    // Variation categories for comprehensive training
    const variationCategories = [
      { name: 'angle', variations: ['from above', 'from below', 'side view', 'three-quarter view', 'front view', 'back view'] },
      { name: 'lighting', variations: ['soft lighting', 'dramatic lighting', 'backlit', 'rim lighting', 'golden hour', 'blue hour', 'studio lighting'] },
      { name: 'expression', variations: ['serene', 'intense', 'contemplative', 'joyful', 'mysterious', 'determined'] },
      { name: 'background', variations: ['simple background', 'detailed environment', 'abstract background', 'natural setting', 'urban environment', 'minimalist'] },
      { name: 'style', variations: ['photographic', 'painterly', 'illustrative', 'cinematic', 'artistic', 'hyperrealistic'] },
      { name: 'mood', variations: ['ethereal', 'dark', 'vibrant', 'melancholic', 'energetic', 'peaceful'] },
      { name: 'detail', variations: ['highly detailed', 'soft focus', 'sharp focus', 'shallow depth of field', 'deep depth of field'] },
    ];

    // Extract key elements from base prompt
    const baseElements = this.extractPromptElements(basePrompt);
    
    for (let i = 0; i < count; i++) {
      const category = variationCategories[i % variationCategories.length];
      const variation = category.variations[Math.floor(Math.random() * category.variations.length)];
      
      let prompt: string;
      
      if (style === 'short') {
        // Short prompts focus on key elements only
        prompt = this.createShortPrompt(baseElements, variation, category.name);
      } else {
        // Detailed prompts include all elements with variations
        prompt = this.createDetailedPrompt(basePrompt, variation, category.name);
      }
      
      prompts.push({
        id: `lora-${Date.now()}-${i}`,
        prompt,
        category: category.name,
        weight: 1.0, // Default weight, can be adjusted
      });
    }
    
    return prompts;
  }

  private static extractPromptElements(prompt: string): string[] {
    // Simple extraction of key elements (in production, this could use NLP)
    const elements = prompt.split(',').map(s => s.trim());
    const keyElements: string[] = [];
    
    // Prioritize subject/character descriptions
    elements.forEach(element => {
      if (element.length > 3 && !element.includes('background') && !element.includes('lighting')) {
        keyElements.push(element);
      }
    });
    
    return keyElements.slice(0, 3); // Keep top 3 key elements
  }

  private static createShortPrompt(baseElements: string[], variation: string, category: string): string {
    const core = baseElements.slice(0, 2).join(', ');
    
    // Add variation based on category
    switch (category) {
      case 'angle':
      case 'lighting':
        return `${core}, ${variation}`;
      case 'style':
      case 'mood':
        return `${variation}, ${core}`;
      default:
        return `${core}, ${variation}`;
    }
  }

  private static createDetailedPrompt(basePrompt: string, variation: string, category: string): string {
    // Remove existing variations of the same category if present
    let modifiedPrompt = basePrompt;
    
    // Add new variation
    switch (category) {
      case 'angle':
        modifiedPrompt = `${modifiedPrompt}, ${variation}, dynamic composition`;
        break;
      case 'lighting':
        modifiedPrompt = `${modifiedPrompt}, ${variation}, professional photography`;
        break;
      case 'expression':
        modifiedPrompt = modifiedPrompt.replace(/serene|intense|contemplative|joyful|mysterious|determined/gi, variation);
        if (!modifiedPrompt.includes(variation)) {
          modifiedPrompt = `${modifiedPrompt}, ${variation} expression`;
        }
        break;
      case 'background':
        modifiedPrompt = `${modifiedPrompt}, ${variation}`;
        break;
      case 'style':
        modifiedPrompt = `${variation} style, ${modifiedPrompt}`;
        break;
      case 'mood':
        modifiedPrompt = `${modifiedPrompt}, ${variation} atmosphere`;
        break;
      case 'detail':
        modifiedPrompt = `${modifiedPrompt}, ${variation}`;
        break;
    }
    
    return modifiedPrompt;
  }

  // Statistics
  static getStats(): PromptEvolutionStats {
    const sessions = this.getAllSessions();
    const agentCounts: Record<string, number> = {};
    let totalIterations = 0;
    let totalImages = 0;

    sessions.forEach(session => {
      totalIterations += session.metadata.totalIterations;
      totalImages += session.metadata.generatedImages?.length || 0;
      
      session.metadata.agentsUsed.forEach(agent => {
        agentCounts[agent] = (agentCounts[agent] || 0) + 1;
      });
    });

    const mostUsedAgents = Object.entries(agentCounts)
      .map(([agent, count]) => ({ agent, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalSessions: sessions.length,
      totalIterations,
      averageIterationsPerSession: sessions.length > 0 ? totalIterations / sessions.length : 0,
      mostUsedAgents,
      totalImagesGenerated: totalImages,
    };
  }
}